import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FEATURE_BILLING,
    FEATURE_WORKGROUPS,
    FlyFreelyError,
    FlyFreelyLoggingService,
    HistoricalPersonDto,
    InviteDto,
    InvitesService,
    NameValue,
    PersonRolesDto,
    PersonService,
    PersonWithRoles,
    PersonnelCriteria,
    PersonnelFieldSort,
    PersonnelFields,
    PersonsOrganisationDto,
    PreferencesService,
    WorkTracker,
    hasAnyPermission,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig,
    TableSetupUserPreferences
} from '@flyfreely-portal-ui/flyfreely-table';
import { WorkgroupsDataService } from '@flyfreely-portal-ui/organisation-admin';
import { FormatColumnArrayPipe } from '@flyfreely-portal-ui/ui';
import { Widget, WidgetRegistration } from '@flyfreely-portal-ui/widget';
import {
    OrganisationSubscriptionsService,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { PersonnelDialoguesService } from 'libs/personnel/src/lib/personnel-dialogues.service';
import { isCurrentSubscription } from 'libs/subscriptions/src/lib/helpers';
import { OrganisationSubscriptionsState } from 'libs/subscriptions/src/lib/interfaces';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subject,
    combineLatest,
    merge,
    of
} from 'rxjs';
import {
    catchError,
    concatMap,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil
} from 'rxjs/operators';
import { WidgetsPreferencesService } from '../widget-preferences.service';
import { PersonnelWidgetDataService } from './personnel-widget-data.service';

interface TablePersonnel {
    lastName?: string;
    firstName?: string;
    email: string;
    phoneNumber?: string;
    status?: string;
    inviteId?: number;
    roles: PersonRolesDto.Roles[];
}

enum View {
    CURRENT = 'CURRENT',
    HISTORICAL = 'HISTORICAL',
    INVITED = 'INVITED'
}

const PERSONNEL_IDENTIFIER = 'personnelList';

@Component({
    selector: 'personnel-widget',
    templateUrl: './personnel-widget.component.html',
    styles: [
        `
            :host {
                scroll-margin-top: 60px;
            }
        `
    ],
    providers: [PersonnelWidgetDataService]
})
export class PersonnelWidgetComponent implements OnInit, OnDestroy, Widget {
    organisation: PersonsOrganisationDto;

    availableColumns: TableColumn[];
    tableConfig: TableConfig;
    selectedColumns: string[];
    inviteTableConfig: TableConfig;
    historicalTableConfig: TableConfig;
    columnSorting: ColumnSortPreferences;
    tableSearch: any;

    canAddPersonnel: boolean;
    canListPersonnel: boolean;
    canReport = false;
    canViewBilling: boolean;
    canUseWorkgroups = false;

    licences: OrganisationSubscriptionsState;

    view$ = new BehaviorSubject(View.CURRENT);

    selectedPersonnelListOptions = [
        {
            value: View.CURRENT,
            name: 'Current Personnel'
        },
        {
            value: View.INVITED,
            name: 'Pending Invites'
        },
        {
            value: View.HISTORICAL,
            name: 'Archived Personnel'
        }
    ];
    selectedPersonnelList: NameValue = {
        value: View.CURRENT,
        name: 'Current Personnel'
    };

    tablePersonnel: TablePersonnel[];
    inviteColumns: string[];

    userPreferences: TableSetupUserPreferences;

    private refreshInvites = new Subject<void>();
    private refreshHistoricalPersonnel = new Subject<void>();

    personnel$ = new Subject<PersonWithRoles[]>();
    historicalPersonnel$: Observable<HistoricalPersonDto[]>;
    invites$: Observable<TablePersonnel[]>;

    public working: boolean;
    private workTracker = new WorkTracker();

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private personService: PersonService,
        private personnelDialoguesService: PersonnelDialoguesService,
        private preferencesService: PreferencesService,
        private subscriptionsService: OrganisationSubscriptionsService,
        private workspaceStateService: WorkspaceStateService,
        private invitesService: InvitesService,
        private formatColumnArrayPipe: FormatColumnArrayPipe,
        private logging: FlyFreelyLoggingService,
        private preferences: WidgetsPreferencesService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private workgroupDataService: WorkgroupsDataService,
        private personnelWidgetDataService: PersonnelWidgetDataService
    ) {
        this.selectedColumns = null;
        this.inviteColumns = ['email', 'roles'];

        this.columnSorting = null;

        this.tableSearch = null;

        this.tableConfig = {
            limit: 10,
            actions: [],
            limitSelection: [10, 25, 50, 100],
            serverPagination: true
        };
        this.inviteTableConfig = {
            limit: 10,
            actions: [
                {
                    action: (item: TablePersonnel) => this.revokeInvite(item),
                    tooltip: 'Revoke invite',
                    icon: 'fal fa-trash-alt'
                }
            ],
            limitSelection: [10, 25, 50, 100],
            serverPagination: false
        };
        this.historicalTableConfig = {
            limit: 10,
            actions: [],
            limitSelection: [10, 25, 50, 100],
            serverPagination: false
        };
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.refreshPermissions();
                this.setupColumns();
                this.loadPreferences();
                this.refreshInvites.next();
                this.refreshHistoricalPersonnel.next();
            });

        combineLatest([
            this.workTracker.observable,
            this.personnelWidgetDataService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, serviceWorking]) => {
                this.working = working || serviceWorking;
            });

        merge(
            this.workgroupDataService.personnelChange$,
            this.personService.change$
        )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.subscriptionsService.refresh();
                this.refreshPersonnel();
            });

        combineLatest([
            this.personnelWidgetDataService.personnel$,
            this.personnelWidgetDataService.currentPage$,
            this.personnelWidgetDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([personnel, currentPage, totalItems]) => {
                this.personnel$.next(personnel);
                if (this.tableConfig != null) {
                    this.tableConfig = {
                        ...this.tableConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });

        this.invites$ = merge(
            this.invitesService.change$,
            this.refreshInvites.pipe(startWith(undefined))
        ).pipe(
            takeUntil(this.ngUnsubscribe$),
            concatMap(() => {
                // This is doubled up here so the subscription service can react to the widget's internal changes too.
                // Removing this causes update issues in the subscription service under certain circumstances.
                this.subscriptionsService.refresh();
                return this.invitesService.findInvites(this.organisation.id);
            }),
            map(invites =>
                invites
                    .filter(
                        invite => invite.status === InviteDto.Status.PENDING
                    )
                    .map(invite => ({
                        email: invite.recipientEmail,
                        inviteId: invite.id,
                        roles: invite.roles
                    }))
            ),
            shareReplay()
        );

        this.historicalPersonnel$ = merge(
            this.workgroupDataService.personnelChange$,
            this.personService.change$,
            this.refreshHistoricalPersonnel.pipe(startWith(undefined))
        ).pipe(
            concatMap(() => {
                if (this.organisation != null) {
                    return this.personService.findHistoricalPersonnel(
                        this.organisation.id
                    );
                }
                return [];
            }),
            takeUntil(this.ngUnsubscribe$)
        );

        combineLatest([
            this.view$,
            this.personnel$,
            this.historicalPersonnel$,
            this.canAddPersonnel ? this.invites$ : of([])
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([view, personnel, historicalPersonnel, invites]) => {
                this.tablePersonnel = (() => {
                    switch (view) {
                        case View.CURRENT:
                            return this.processPersonnelData(personnel);
                        case View.HISTORICAL:
                            return historicalPersonnel;
                        case View.INVITED:
                            return invites;
                    }
                })();
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshPersonnel() {
        if (this.organisation == null || this.view$.value !== View.CURRENT) {
            return;
        }

        const criteria: Partial<PersonnelCriteria> = {};
        if (this.tableSearch != null) {
            if (this.tableSearch.firstName) {
                criteria.firstName = this.tableSearch.firstName;
            }
            if (this.tableSearch.lastName) {
                criteria.lastName = this.tableSearch.lastName;
            }
            if (this.tableSearch.email) {
                criteria.email = this.tableSearch.email;
            }
            if (this.tableSearch.roles) {
                criteria.role = this.tableSearch.roles;
            }
        }

        let sortFields: PersonnelFieldSort[];
        if (this.columnSorting) {
            const fieldMapping: Record<string, PersonnelFields> = {
                firstName: PersonnelFields.FIRST_NAME,
                lastName: PersonnelFields.LAST_NAME,
                email: PersonnelFields.EMAIL
            };

            const field = fieldMapping[this.columnSorting.column];
            if (field) {
                sortFields = [
                    {
                        field,
                        order: this.columnSorting.ascending ? 'ASC' : 'DESC'
                    }
                ];
            }
        }

        this.personnelWidgetDataService.findPersonnel(
            this.organisation.id,
            this.tableConfig?.currentPage ?? 0,
            this.tableConfig?.limit ?? 10,
            criteria,
            sortFields
        );
    }

    private processPersonnelData(personnel: PersonWithRoles[]) {
        if (!this.canUseWorkgroups) {
            return personnel;
        }
        return personnel.map(person => ({
            ...person,
            workgroupNameList: person.workgroupList?.map(w => w.name) ?? []
        }));
    }

    private setupColumns() {
        const roles = this.personService.getPersonRoles();
        const roleSearchOptions = Object.keys(roles).map(r => ({
            value: r,
            name: roles[r]
        }));
        this.availableColumns = [
            {
                value: 'lastName',
                name: 'Last Name',
                searchable: true,
                selectable: false,
                defaultSelection: false,
                sortable: true,
                linkFunction: (person: PersonRolesDto) =>
                    this.showPerson(person)
            },
            {
                value: 'firstName',
                name: 'First Name',
                searchable: true,
                selectable: false,
                defaultSelection: false,
                sortable: true,
                linkFunction: (person: PersonRolesDto) =>
                    this.showPerson(person)
            },
            {
                value: 'email',
                name: 'Email',
                searchable: true,
                selectable: true,
                defaultSelection: false,
                sortable: true
            },
            {
                value: 'phoneNumber',
                name: 'Phone Number',
                searchable: false,
                selectable: true,
                defaultSelection: false,
                sortable: false
            },
            {
                value: 'roles',
                name: 'Roles',
                searchable: true,
                selectable: true,
                defaultSelection: true,
                sortable: false,
                searchOptions: roleSearchOptions,
                searchFunction:
                    (roleSearch: PersonRolesDto.Roles) =>
                    (roles: PersonRolesDto.Roles[]) =>
                        roles.find(r => r === roleSearch) != null,
                formatterFunction: (a: PersonRolesDto.Roles[]) =>
                    a == null
                        ? null
                        : this.formatColumnArrayPipe.transform(
                              a.map(r => roles[r]),
                              2
                          )
            },
            ...(this.canUseWorkgroups
                ? [
                      {
                          value: 'workgroupNameList',
                          name: 'Workgroup',
                          searchable: false,
                          selectable: true,
                          defaultSelection: true,
                          sortable: false,
                          formatterFunction: (a: string[]) =>
                              a == null
                                  ? null
                                  : this.formatColumnArrayPipe.transform(a, 2)
                      } as TableColumn
                  ]
                : [])
        ];
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }
        this.preferences
            .findPreferences('personnelListWidgetTable', this.organisation.id)
            .pipe(
                switchMap(preferences => {
                    if (preferences == null) {
                        return this.preferences
                            .findLegacyPreferences(
                                'personnelListWidgetColumns',
                                this.organisation.id
                            )
                            .pipe(
                                map(pref => ({
                                    selectedColumns: pref,
                                    columnSorting: null,
                                    tableSearch: null,
                                    itemLimit: this.tableConfig.limit
                                })),
                                catchError(() => {
                                    this.handleDefaultPreferences();
                                    return of(null);
                                })
                            );
                    } else {
                        return of(preferences);
                    }
                }),
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                this.applyPreferences(preferences);
                this.refreshPersonnel();
            })
            .add(this.workTracker.createTracker());
    }

    private handleDefaultPreferences() {
        this.applyPreferences({});
    }

    private applyPreferences(preferences: TableSetupUserPreferences) {
        this.userPreferences = this.preferences.applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );

        this.selectedColumns = this.userPreferences.selectedColumns;
        this.columnSorting = this.userPreferences.columnSorting;
        this.tableSearch = this.userPreferences.tableSearch;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.historicalTableConfig.limit = this.userPreferences.itemLimit;
        this.inviteTableConfig.limit = this.userPreferences.itemLimit;
    }

    changeStatus() {
        const newView = this.selectedPersonnelList.value as View;
        this.view$.next(newView);
        if (this.selectedPersonnelList.value === View.CURRENT) {
            this.tableConfig.currentPage = 0;
            this.refreshPersonnel();
        }
    }

    revokeInvite(item: TablePersonnel) {
        this.invitesService
            .retractInvite(item.inviteId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result =>
                    this.logging.success(`Invite revoked for ${item.email}`),
                error: (error: FlyFreelyError) => this.logging.error(error)
            })
            .add(this.workTracker.createTracker());
    }

    private refreshPermissions() {
        this.canListPersonnel = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.PERSON_LIST
        );
        this.canAddPersonnel = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.PERSON_ADD
        );

        this.canViewBilling = hasFeatureFlag(
            this.organisation,
            FEATURE_BILLING
        );
        this.canUseWorkgroups = hasFeatureFlag(
            this.organisation,
            FEATURE_WORKGROUPS
        );
        this.refreshLicences();
    }

    private refreshLicences() {
        if (this.canAddPersonnel) {
            this.subscriptionsService.currentSubscriptions$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(state => (this.licences = state));
        }
    }

    showPerson(person: PersonRolesDto) {
        this.personnelDialoguesService.showPersonDetailsDialogue(
            person.id,
            this.organisation.id,
            person.availableActions != null
                ? person.availableActions.canEdit
                : false
        );
    }

    newPerson() {
        this.personnelDialoguesService.showAddPersonDialogue(
            this.organisation.id
        );
    }

    showPersonnelReportsDialogue() {
        const rolesConverter = this.personService.getPersonRoles();
        const personnel = this.tablePersonnel.map((person: TablePersonnel) => ({
            ...person,
            roles: person.roles.map(a => rolesConverter[a])
        }));

        this.personnelDialoguesService.showPersonnelReportsDialogue(
            this.organisation.id,
            personnel
        );
    }

    showOrganisationSubscriptionSettings() {
        this.router.navigate(['orgadmin', 'subscriptions'], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge'
        });
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.columnSorting != null &&
            !this.selectedColumns.includes(this.columnSorting.column)
        ) {
            this.columnSorting = null;
        }
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.columnSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting = sorting;
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        if (this.view$.value === View.CURRENT) {
            this.tableConfig.currentPage = 0;
            this.refreshPersonnel();
        }
        this.updateUserPreferences();
    }

    updateSearchPreferences(search: any) {
        this.tableSearch = search;
        this.userPreferences = {
            ...this.userPreferences,
            tableSearch: search
        };
        if (this.view$.value === View.CURRENT) {
            this.tableConfig.currentPage = 0;
            this.refreshPersonnel();
        }
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        if (this.view$.value === View.CURRENT) {
            this.refreshPersonnel();
        }
        this.updateUserPreferences();
    }

    updateItemLimitForHistorical(limit: number) {
        this.historicalTableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    updateItemLimitForInvites(limit: number) {
        this.inviteTableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    updateUserPreferences() {
        // remove status from preferences so table always defaults to current personnel
        this.userPreferences = {
            ...this.userPreferences,
            tableSearch: {
                ...this.userPreferences.tableSearch,
                status: null
            }
        };
        this.preferencesService
            .updatePreferences(
                'personnelListWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    onTablePageChanged(page: number) {
        if (this.view$.value === View.CURRENT) {
            this.tableConfig.currentPage = page;
            this.refreshPersonnel();
        }
    }

    isCurrentSubscription = isCurrentSubscription;
}

export const personnelWidgetRegistration: WidgetRegistration = {
    widgetIdentifier: PERSONNEL_IDENTIFIER,
    component: PersonnelWidgetComponent,
    isAvailable: organisation =>
        hasAnyPermission(organisation, [
            PersonsOrganisationDto.Permissions.PERSON_LIST
        ])
};
